import { PayPalButtons } from "@paypal/react-paypal-js"
import { useRef } from "react"
import PropTypes from "prop-types"
import { useDispatch, useSelector } from "react-redux"
import { createPayPalOrder, capturePayPalOrder } from "store/payment-provider"
import { useNavigate } from "react-router-dom"
import { routes } from "router"
import { oldRoutes } from "router/old-routes"
import { toaster } from "components/common/Toast"
import { useTranslation } from "react-i18next"

const PaypalPayment = ({ bookingId, bookingUuid, promoCodes = [] }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { loading: isCaptureLoading } = useSelector((state) => state.paymentProvider.capturePayPalOrder)
  const userRole = useSelector((state) => state.user.role)
  const { loading: isCreateLoading } = useSelector((state) => state.paymentProvider.createPayPalOrder)

  // Set when create_order itself fails: api() has already toasted the server's message,
  // so onError must not add a second, raw "Request failed with status code 400" toast.
  const isApiErrorShownRef = useRef(false)

  const createOrder = () => {
    isApiErrorShownRef.current = false
    const payload = { trip_booking_id: bookingId }
    if (promoCodes?.length) payload.promo_codes = promoCodes
    return dispatch(createPayPalOrder(payload))
      .then((res) => res?.order_id)
      .catch((error) => {
        isApiErrorShownRef.current = true
        throw error
      })
  }

  const onApprove = (data) => {
    if (!data?.orderID) return
    dispatch(capturePayPalOrder({ order_id: data.orderID, trip_booking_id: bookingId })).then((res) => {
      if (res?.success) {
        toaster.success(t("booking.paypal_payment_completed"))
        if (userRole === "user") {
          navigate(routes?.clientBookingConfirmation({ id: bookingUuid }))
        } else {
          navigate(oldRoutes?.bookingConfirmation({ id: bookingUuid }))
        }
      }
    })
  }

  const handleCancel = () => {
    toaster.warn(t("booking.paypal_payment_cancelled"))
  }

  const handleError = (error) => {
    if (isApiErrorShownRef.current) return
    toaster.error(error?.message || t("booking.paypal_payment_failed"))
  }

  return (
    <PayPalButtons
      fundingSource="paypal"
      createOrder={createOrder}
      onApprove={onApprove}
      onCancel={handleCancel}
      onError={handleError}
      disabled={isCreateLoading || isCaptureLoading}
      className="mt-m"
    />
  )
}

PaypalPayment.propTypes = {
  bookingId: PropTypes.string,
  bookingUuid: PropTypes.string,
  promoCodes: PropTypes.arrayOf(PropTypes.string)
}

export default PaypalPayment
